import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ScrollMagic from "scrollmagic";
import { Helmet } from "react-helmet-async";

// Error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("PrivacyPolicy component error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-fallback" style={{ padding: "2rem", textAlign: "center" }}>
          <h2>Something went wrong.</h2>
          <p>Please refresh the page or try again later.</p>
        </div>
      );
    }

    return this.props.children;
  }
}

const PrivacyPolicy = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [translationError, setTranslationError] = useState(false);

  let t;
  try {
    const translation = useTranslation();
    t = translation.t;
  } catch (error) {
    console.error("Translation hook error:", error);
    setTranslationError(true);
    t = (key) => key; // Fallback function
  }

  useEffect(() => {
    let controller = null;
    let scene = null;

    try {
      window.scrollTo(0, 0);
      controller = new ScrollMagic.Controller();

      const initializeScrollMagic = () => {
        try {
          const triggerElement = document.getElementById("triggerPinned");

          if (window.innerWidth > 768 && triggerElement) {
            scene = new ScrollMagic.Scene({
              triggerElement: triggerElement,
              triggerHook: 0,
            })
              .setPin(triggerElement)
              .addTo(controller);
          }
          setIsLoading(false);
        } catch (error) {
          console.error("ScrollMagic initialization error:", error);
          setIsLoading(false);
        }
      };

      // Use setTimeout to ensure DOM is ready
      const timeoutId = setTimeout(initializeScrollMagic, 100);

      return () => {
        clearTimeout(timeoutId);
        try {
          if (scene) {
            scene.destroy(true);
          }
          if (controller) {
            controller.destroy(true);
          }
        } catch (error) {
          console.error("Cleanup error:", error);
        }
      };
    } catch (error) {
      console.error("useEffect initialization error:", error);
      setIsLoading(false);
    }
  }, []);

  if (isLoading) {
    return <div style={{ padding: "2rem", textAlign: "center" }}>Loading...</div>;
  }

  return (
    <ErrorBoundary>
      <div>
        <Helmet>
          <title>Owision Privacy Policy | Data Protection & Security</title>
          <meta
            name="description"
            content="Read Owision's Privacy Policy to understand how we collect, use, and protect your personal data while ensuring transparency and security online."
          />
        </Helmet>
        <section className="privacy-banner" id="triggerPinned">
          <h1 className="">Privacy policy</h1>
        </section>
        <section>
          <div className="tnc_container white-section">
            <div className="policy_text">
              <p>
                "This privacy policy concerns Owision AB and its website:
                <a target="_blank" rel="noreferrer" href="https://owision.com/">
                  {" "}
                  https://owision.com/
                </a>
                <br />
              </p>
              <h5>What personal data we collect and why we collect it</h5>
              <br />
              <h4>Cookies</h4>
              <br />
              <p>
                A "cookie" is a small text file that is stored on a computer for record-keeping
                purposes. We use cookies for analyzing trends, site administration, tracking user
                movement, and to gather demographic information from our base as a whole. Some
                cookies remain on your computer until you delete them. Other, like session ID
                cookies, expire when you close your browser. You may set your browser setting to
                attempt to reject cookies and you may still use the Service, however, certain
                features of the Service may not be available if your browser does not accept
                cookies. This site make use of Google Analytics which collects, stores and analyses
                web traffic data to help understanding how people are using the website.
                <br />
              </p>
              <br />
              <h4>Pixels</h4>
              <br />
              <p>
                "Pixels" are tiny graphics with a unique identifier that are used to track the
                online movements of web users. Unlike cookies, which are stored on a computer's hard
                drive, pixels are small graphics that are about the size of the period at the end of
                the sentence that are embedded invisibly on web pages or in HTML-based emails. Our
                third-party analytics providers may place pixels on the Site that track what other
                websites you visit (both before and after visiting the Site). Our third-party
                analytics providers use information obtained from pixels to help us improve our
                business and the Service. We do not control the use of pixels by third parties.
                <br />
              </p>
              <br />
              <h4>Third Party Analytics Providers</h4>
              <br />
              <p>
                We use third parties to help us operate and improve the Service. In doing so, we may
                share with these third parties non-personally identifiable information about users'
                use of the Service. These third party providers also use cookies. We use third party
                analytics providers, such as Google Analytics, to provide us with general
                demographic and interest-level information about our users and to help create a
                better user experience. We do not control information collected by third parties and
                are not responsible for their use of that information
                <br />
              </p>
              <br />
              <h4>Embedded content from other websites</h4>
              <br />
              <p>
                Articles on this site may include embedded content (e.g. videos, images, articles,
                etc.). Embedded content from other websites behaves in the exact same way as if the
                visitor has visited the other website. These websites may collect data about you,
                use cookies, embed additional third-party tracking, and monitor your interaction
                with that embedded content, including tracking your interaction with the embedded
                content if you have an account and are logged in to that website.
                <br />
              </p>
              <br />
              <h4>Analytics</h4>
              <br />
              <p>
                This site makes use of Google Analytics which collects, stores and analyses web
                traffic data to help understanding how people are using the website. Google
                Analytics uses cookies and pixels in order to collect demographic and interest-level
                information and usage information from users that visit the Service, including but
                not limited to information about the pages where users enter and exit the Service
                and what pages users view on the Service, time spent, browser, operating system, and
                IP address. Cookies and pixels allow Google to recognize a user when a user visits
                the Service and when the user visits other websites. Google uses the information it
                collects from the Service and other websites to share with us and other website
                operators information about users including, but not limited to, age range, gender,
                geographic regions, general interests, and details about devices used to visit
                websites and purchase items. We take reasonable measures to prevent linking of
                information we receive from Google with any of your personally identifiable
                information. For more information regarding Google's use of cookies and collection
                and use of information see the&nbsp;
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://web.archive.org/web/20220126142746/https://www.google.com/policies/privacy/"
                >
                  Google Privacy Policy
                </a>
                .
                <br />
              </p>
              <br />
              <p>What rights you have over your data</p>
              <br />
              <p>
                If you have an account on this site, or have left comments, you can request to
                receive an exported file of the personal data we hold about you, including any data
                you have provided to us. You can also request that we erase any personal data we
                hold about you. This does not include any data we are obliged to keep for
                administrative, legal, or security purposes.
                <br />
              </p>
              <br />
              <p>
                Visitor data may be checked through an automated spam detection service and be sent
                to third-party products.
              </p>
            </div>
          </div>
        </section>
      </div>
    </ErrorBoundary>
  );
};

export default PrivacyPolicy;
